"use client";

import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  XIcon,
  SearchIcon,
  LayoutGridIcon,
  TextQuoteIcon,
  BookOpenIcon,
  FilesIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { usePdfDocumentStore } from "./pdfDocumentStore";
import { useGeneratedDocumentStore } from "@/components/document/generatedDocumentStore";
import { PdfDocumentTabs } from "./PdfDocumentTabs";
import { PdfViewerCore, PdfViewerProvider } from "./PdfViewerCore";
import { PdfToolbar } from "./PdfToolbar";
import { PdfSidebarViews } from "./PdfSidebarViews";
import { PdfSearchBar } from "./PdfSearchBar";
import { PdfDiffView } from "./PdfDiffView";
import { openPraetorCaseDocument } from "@/lib/authFetch";
import { useAuth } from "@/lib/auth/AuthContext";
import {
  VersionStepper,
  PdfDiffToggle,
} from "./GeneratedDocControls";
import { DocumentActionBar } from "@/components/document/DocumentActionBar";
import { PdfViewModeContext } from "./pdfViewModeContext";
import type { PdfSidebarView } from "./types";

// ─── Resize handle ───────────────────────────────────────────────────────────

function ResizeHandle() {
  const setPanelWidth = usePdfDocumentStore((s) => s.setPanelWidth);
  const panelWidth = usePdfDocumentStore((s) => s.panelWidth);
  const dragging = useRef(false);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragging.current = true;

      const startX = e.clientX;
      const startWidth = panelWidth;

      function onMouseMove(ev: MouseEvent) {
        if (!dragging.current) return;
        const delta = startX - ev.clientX;
        const maxW = window.innerWidth * 0.6;
        const newWidth = Math.max(300, Math.min(maxW, startWidth + delta));
        setPanelWidth(newWidth);
      }

      function onMouseUp() {
        dragging.current = false;
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [panelWidth, setPanelWidth],
  );

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      onMouseDown={onMouseDown}
      className="absolute inset-y-0 left-0 z-20 w-1 cursor-col-resize transition-colors hover:bg-primary/30"
    />
  );
}

// ─── Header bar ──────────────────────────────────────────────────────────────

const PanelHeader = memo(function PanelHeader({
  onToggleSearch,
}: {
  onToggleSearch: () => void;
}) {
  const { t } = useTranslation();
  const closeAllDocuments = usePdfDocumentStore((s) => s.closeAllDocuments);
  const activeDocumentId = usePdfDocumentStore((s) => s.activeDocumentId);
  const documents = usePdfDocumentStore((s) => s.documents);
  const activeDoc = documents.find((d) => d.id === activeDocumentId);
  const sidebarView = usePdfDocumentStore((s) => s.sidebarView);
  const setSidebarView = usePdfDocumentStore((s) => s.setSidebarView);
  const citationSnippets = usePdfDocumentStore((s) => s.citationSnippets);
  const activeCitationSnippet = usePdfDocumentStore((s) => s.activeCitationSnippet);
  const setActiveCitationSnippet = usePdfDocumentStore((s) => s.setActiveCitationSnippet);

  const activeCitations = useMemo(
    () =>
      activeDocumentId
        ? [...(citationSnippets[activeDocumentId] ?? [])].sort((a, b) => (a.page ?? 0) - (b.page ?? 0))
        : [],
    [activeDocumentId, citationSnippets],
  );

  const [toast, setToast] = useState<{ msg: string; tick: number } | null>(null);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast?.tick]);

  const toggleThumbnails = useCallback(() => {
    const next: PdfSidebarView = sidebarView === "thumbnails" ? "none" : "thumbnails";
    setSidebarView(next);
  }, [sidebarView, setSidebarView]);

  const toggleDocuments = useCallback(() => {
    const next: PdfSidebarView = sidebarView === "documents" ? "none" : "documents";
    setSidebarView(next);
  }, [sidebarView, setSidebarView]);

  // Render the "Documents" toggle only when the thread has at least one generated
  // doc — avoids surfacing an empty list affordance for citation-only sessions.
  const hasGeneratedDocs = useGeneratedDocumentStore(
    (s) => Object.keys(s.entries).length > 0,
  );

  const isGenerated = activeDoc?.kind === "generated";
  const { scheme } = useAuth();
  const showOpenInPraetor = scheme === "Bearer" && !isGenerated;

  return (
    <>
      <div className="flex h-11 shrink-0 items-center gap-1 border-b border-border/30 bg-background/80 backdrop-blur-sm px-2.5">
        {/* Left group */}
        <div className="flex items-center gap-0.5">
          <TooltipIconButton
            onClick={closeAllDocuments}
            tooltip={t("pdf.closePanel")}
            side="bottom"
            className="size-7 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer"
          >
            <XIcon className="size-3.5" />
          </TooltipIconButton>

          <span className="mx-1 h-4 w-px bg-border/30" />

          <TooltipIconButton
            onClick={toggleThumbnails}
            tooltip={t("pdf.thumbnails")}
            side="bottom"
            className={cn(
              "size-7 rounded-lg cursor-pointer hover:bg-muted",
              sidebarView === "thumbnails"
                ? "bg-muted text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <LayoutGridIcon className="size-3.5" />
          </TooltipIconButton>

          {hasGeneratedDocs && (
            <TooltipIconButton
              onClick={toggleDocuments}
              tooltip={t("pdf.threadDocuments")}
              side="bottom"
              className={cn(
                "size-7 rounded-lg cursor-pointer hover:bg-muted",
                sidebarView === "documents"
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <FilesIcon className="size-3.5" />
            </TooltipIconButton>
          )}

          {showOpenInPraetor && (
            <TooltipIconButton
              tooltip={t("pdf.openInPraetor")}
              side="bottom"
              className="size-7 rounded-lg cursor-pointer text-muted-foreground hover:text-foreground hover:bg-muted"
              onClick={() => {
                const state = usePdfDocumentStore.getState();
                const docId = state.activeDocumentId;
                if (docId) {
                  const doc = state.documents.find((d) => d.id === docId);
                  openPraetorCaseDocument(docId);
                  setToast({ msg: t("pdf.openingInPraetor", { name: doc?.title ?? "dokument" }), tick: Date.now() });
                }
              }}
            >
              <img src="/open-icon.png" alt="" className="size-4 rounded object-cover" />
            </TooltipIconButton>
          )}

          {/* Citations popover */}
          {activeCitations.length > 0 && (
            <PopoverPrimitive.Root>
              <PopoverPrimitive.Trigger asChild>
                <TooltipIconButton
                  tooltip={t("pdf.citations")}
                  side="bottom"
                  className="size-7 rounded-lg cursor-pointer relative text-muted-foreground hover:text-foreground hover:bg-muted"
                >
                  <TextQuoteIcon className="size-3.5" />
                  <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-3.5 h-3.5 rounded-full bg-primary text-primary-foreground text-[8px] font-bold leading-none px-0.5">
                    {activeCitations.length}
                  </span>
                </TooltipIconButton>
              </PopoverPrimitive.Trigger>
              <PopoverPrimitive.Portal>
                <PopoverPrimitive.Content
                  side="bottom"
                  align="start"
                  sideOffset={8}
                  className="z-50 w-72 max-h-80 overflow-y-auto rounded-xl border border-border/50 bg-popover/95 backdrop-blur-md shadow-lg outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 [&::-webkit-scrollbar]:hidden"
                  style={{ scrollbarWidth: "none" }}
                >
                  <div className="px-3 py-2 border-b border-border/30">
                    <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                      {t("pdf.citationsCount", { count: String(activeCitations.length) })}
                    </span>
                  </div>
                  <div className="py-1">
                    {activeCitations.map((citation, i) => (
                      <PopoverPrimitive.Close key={i} asChild>
                        <button
                          type="button"
                          className={cn(
                            "flex w-full flex-col gap-0.5 px-3 py-2 text-left transition-colors hover:bg-muted/60 cursor-pointer rounded-md mx-1",
                            citation.snippet === activeCitationSnippet && "bg-muted/50",
                          )}
                          style={{ width: "calc(100% - 0.5rem)" }}
                          onClick={() => setActiveCitationSnippet(citation.snippet)}
                        >
                          <span className="text-xs text-foreground/80 line-clamp-2 leading-snug">
                            &ldquo;{citation.snippet}&rdquo;
                          </span>
                        </button>
                      </PopoverPrimitive.Close>
                    ))}
                  </div>
                </PopoverPrimitive.Content>
              </PopoverPrimitive.Portal>
            </PopoverPrimitive.Root>
          )}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right group: Generated-doc controls */}
        {isGenerated && activeDoc && (
          <div className="flex items-center gap-1.5">
            <VersionStepper docId={activeDoc.documentId ?? activeDoc.id} title={activeDoc.title} activeVersionId={activeDoc.versionId} />
            <PdfDiffToggle docId={activeDoc.documentId ?? activeDoc.id} versionId={activeDoc.versionId} />
            <span className="mx-1 h-4 w-px bg-border/30" />
            <DocumentActionBar docId={activeDoc.documentId ?? activeDoc.id} title={activeDoc.title} versionId={activeDoc.versionId} />
          </div>
        )}

        <span className="mx-1 h-4 w-px bg-border/30" />

        <TooltipIconButton
          onClick={onToggleSearch}
          tooltip={t("pdf.search")}
          side="bottom"
          className="size-7 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer"
        >
          <SearchIcon className="size-3.5" />
        </TooltipIconButton>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 shadow-lg dark:border-blue-800/50 dark:bg-blue-950/80 dark:text-blue-300">
            <BookOpenIcon className="size-4 shrink-0" />
            <span>{toast.msg}</span>
          </div>
        </div>
      )}
    </>
  );
});

// ─── Panel content (shared between desktop & mobile) ─────────────────────────

const PdfPanelContent = memo(function PdfPanelContent() {
  const activeDocumentId = usePdfDocumentStore((s) => s.activeDocumentId);
  const documents = usePdfDocumentStore((s) => s.documents);
  const activeDoc = documents.find((d) => d.id === activeDocumentId);
  const [searchOpen, setSearchOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"pdf" | "diff">("pdf");
  const diffIframeRef = useRef<HTMLIFrameElement | null>(null);
  const [diffZoom, setDiffZoom] = useState(1.0);

  // Reset to PDF view on doc OR version change. Diff is per-version, so stepping
  // from v2 → v3 should drop the user back into the PDF for the new version
  // rather than show v2's stale (or simply wrong) diff.
  useEffect(() => {
    setViewMode("pdf");
  }, [activeDocumentId, activeDoc?.versionId]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const showDiffView = viewMode === "diff" && activeDoc?.kind === "generated";

  return (
    <PdfViewModeContext.Provider value={{ viewMode, setViewMode, diffIframeRef, diffZoom, setDiffZoom }}>
      <PdfViewerProvider>
        <div className="flex h-full flex-col bg-background rounded-l-xl overflow-hidden">
          {/* Header with all controls */}
          <PanelHeader onToggleSearch={() => setSearchOpen((prev) => !prev)} />

          {/* Tabs */}
          <PdfDocumentTabs />

          {/* Body: sidebar views + viewer (or diff view) */}
          <div className="flex flex-1 overflow-hidden">
            <PdfSidebarViews />
            <div className="relative flex flex-1 flex-col bg-muted/20">
              <PdfSearchBar open={searchOpen} onClose={() => setSearchOpen(false)} />
              <div className={cn("flex flex-1 flex-col min-h-0", showDiffView && "hidden")}>
                <PdfViewerCore />
              </div>
              {showDiffView && activeDoc && (
                <div className="flex flex-1 flex-col min-h-0">
                  <PdfDiffView
                    docId={activeDoc.documentId ?? activeDoc.id}
                    versionId={activeDoc.versionId}
                    iframeRef={diffIframeRef}
                    onZoomChange={setDiffZoom}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Bottom toolbar: page nav + zoom only */}
          <PdfToolbar />
        </div>
      </PdfViewerProvider>
    </PdfViewModeContext.Provider>
  );
});

// ─── Main panel ──────────────────────────────────────────────────────────────

export const PdfSidebarPanel = memo(function PdfSidebarPanel() {
  const { t } = useTranslation();
  const panelOpen = usePdfDocumentStore((s) => s.panelOpen);
  const panelWidth = usePdfDocumentStore((s) => s.panelWidth);
  const documents = usePdfDocumentStore((s) => s.documents);
  const isMobile = useIsMobile();

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "p" && (e.metaKey || e.ctrlKey) && e.shiftKey) {
        e.preventDefault();
        usePdfDocumentStore.getState().closeAllDocuments();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  if (documents.length === 0) return null;

  if (isMobile) {
    return (
      <Sheet open={panelOpen} onOpenChange={(open) => { if (!open) usePdfDocumentStore.getState().closeAllDocuments(); }}>
        <SheetContent side="right" className="w-full p-0 sm:max-w-lg">
          <SheetHeader className="sr-only">
            <SheetTitle>{t("pdf.viewer")}</SheetTitle>
            <SheetDescription>{t("pdf.viewerDescription")}</SheetDescription>
          </SheetHeader>
          <PdfPanelContent />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      data-slot="pdf-panel"
      data-state={panelOpen ? "expanded" : "collapsed"}
      className={cn(
        "relative shrink-0 border-l border-border/30 transition-[width,opacity] duration-200 ease-linear overflow-hidden shadow-[-4px_0_12px_-4px_rgba(0,0,0,0.08)]",
        panelOpen ? "opacity-100" : "w-0 opacity-0",
      )}
      style={panelOpen ? { width: panelWidth } : undefined}
    >
      <ResizeHandle />
      <PdfPanelContent />
    </div>
  );
});
